import { useEffect, useState } from "react";
import { AlertTriangle, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

function Figure({ label, value, zero, testid }) {
  return (
    <div className={`rounded-xl p-2 text-center border ${zero ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-200"}`} data-testid={testid}>
      <p className={`text-lg font-extrabold ${zero ? "text-amber-700" : "text-[#1e3a5f]"}`}>{value}</p>
      <p className="text-[10px] text-slate-500">{label}{zero && <span className="font-bold text-amber-700"> · ZERO</span>}</p>
    </div>
  );
}

function Warnings({ pnl, reach }) {
  const o = pnl.open_items || {};
  const items = [];
  if (reach <= 0) items.push("Footfall is 0 — no tickets scanned/consolidated and no manual adjustment.");
  if (!pnl.revenue.total) items.push("Revenue is €0.00.");
  if (!pnl.costs.total) items.push("Costs are €0.00.");
  if (o.pending_expenses?.count) items.push(`${o.pending_expenses.count} expense claim(s) still pending approval (${eur(o.pending_expenses.amount)}) — they will not be in the locked P&L.`);
  if (o.approved_unpaid_expenses?.count) items.push(`${o.approved_unpaid_expenses.count} approved expense(s) not yet paid (${eur(o.approved_unpaid_expenses.amount)}).`);
  (o.unsettled_payouts || []).forEach(p => items.push(`Merchant payout to ${p.merchant_name} not settled (${eur(p.remaining)} open).`));
  if (!items.length) return null;
  return (
    <ul className="space-y-1 rounded-xl bg-amber-50 border border-amber-300 p-3" data-testid="settle-warnings">
      {items.map(t => <li key={t} className="text-xs text-amber-800 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{t}</li>)}
    </ul>
  );
}

export default function SettleConfirmDialog({ open, pnl, reach, onCancel, onConfirm, busy }) {
  const [comment, setComment] = useState("");
  useEffect(() => { if (open) setComment(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md border-2 border-red-500 max-h-[90vh] overflow-y-auto" data-testid="settle-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-6 h-6" /> This cannot be undone</DialogTitle>
        </DialogHeader>
        <div className="rounded-xl bg-red-600 text-white p-3 text-sm font-semibold" data-testid="settle-irreversible-banner">
          Settling “{pnl.event.name}” permanently locks the P&L. No income, expenses or payouts can be changed afterwards.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Figure label="Footfall" value={reach} zero={reach <= 0} testid="settle-fig-footfall" />
          <Figure label="Revenue" value={eur(pnl.revenue.total)} zero={!pnl.revenue.total} testid="settle-fig-revenue" />
          <Figure label="Costs" value={eur(pnl.costs.total)} zero={!pnl.costs.total} testid="settle-fig-costs" />
          <Figure label="Net P&L" value={eur(pnl.net_pnl)} zero={!pnl.net_pnl} testid="settle-fig-net" />
        </div>
        <Warnings pnl={pnl} reach={reach} />
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Settlement comment (required)</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder="e.g. Figures reviewed with treasurer; no footfall because event was cancelled"
            className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="settle-comment-input" />
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border font-semibold text-sm" data-testid="settle-cancel-btn">Cancel</button>
          <button disabled={!comment.trim() || busy} onClick={() => onConfirm(comment.trim())} data-testid="settle-confirm-btn"
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-40">
            <Lock className="w-4 h-4" /> {busy ? "Settling…" : "Settle & lock"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
